import { Components } from '@/api/generated/api';
import { BillingPlanSwitchEffect } from '@/api/generated/api.enum';
import type { TTranslate } from '@/interfaces';
import { dateTodMMMMyyyy, dateTodMMMyyyy } from '@/utils/date.utils';
import { buildPdfFileName } from 'common/utils/file.utils';
import { formatBillingAmount } from './formatting';
import { productMetaByProduct } from './product-catalog';
import {
  BILLING_STATUS_COLOR_MUTED,
  BILLING_STATUS_COLOR_SUCCESS,
  BILLING_STATUS_COLOR_WARNING,
  type BillingInvoiceStatusPresentation,
} from './types';

export function getPlanSwitchEffectDescription(
  preview: Components.Schemas.BillingPlanSwitchPreviewDtoData,
  t: TTranslate,
): string {
  if (preview.effect === BillingPlanSwitchEffect.periodEnd) {
    return t('billing.planSwitchConfirm.periodEndEffect', {
      date: preview.effectiveAt ? dateTodMMMMyyyy(preview.effectiveAt) : t('billing.planSwitchConfirm.nextPeriod'),
    });
  }

  if (preview.createsImmediateInvoice) {
    if (typeof preview.amountDueNowCents === 'number') {
      if (preview.amountDueNowCents > 0) {
        return t('billing.planSwitchConfirm.immediateWithAmountEffect', {
          amount: formatBillingAmount(preview.amountDueNowCents, preview.currency),
        });
      }

      return t('billing.planSwitchConfirm.immediateWithoutPaymentEffect');
    }

    return t('billing.planSwitchConfirm.immediateWithInvoiceEffect');
  }

  return t('billing.planSwitchConfirm.immediateEffect');
}

export function getBillingInvoiceStatusPresentation(
  invoice: Components.Schemas.BillingInvoiceDto,
  t: TTranslate,
): BillingInvoiceStatusPresentation {
  if (invoice.status === 'paid') {
    return {
      color: BILLING_STATUS_COLOR_SUCCESS,
      label: t('billing.invoices.status.paid'),
      theme: 'fill',
    };
  }

  const fallbackStatus = ['uncollectible', 'void'].includes(invoice.status) ? invoice.status : null;
  if (fallbackStatus) {
    return {
      color: BILLING_STATUS_COLOR_MUTED,
      label: t(`billing.invoices.status.${fallbackStatus}`),
      theme: 'transparent',
    };
  }

  if (invoice.amountRemaining <= 0) {
    return {
      color: BILLING_STATUS_COLOR_SUCCESS,
      label: t('billing.invoices.status.paid'),
      theme: 'fill',
    };
  }

  if (['draft', 'open'].includes(invoice.status) || invoice.amountRemaining > 0) {
    return {
      color: BILLING_STATUS_COLOR_WARNING,
      label: t('billing.invoices.status.open'),
      theme: 'fill',
    };
  }

  return {
    color: BILLING_STATUS_COLOR_MUTED,
    label: t('billing.invoices.status.unknown'),
    theme: 'transparent',
  };
}

export function getBillingInvoiceTitle(invoice: Components.Schemas.BillingInvoiceDto, t: TTranslate): string {
  return invoice.invoiceNumber
    ? t('billing.invoices.invoiceTitle', { number: invoice.invoiceNumber })
    : t('billing.invoices.fallbackInvoiceTitle');
}

export function getBillingInvoiceDateLabel(invoice: Components.Schemas.BillingInvoiceDto, t: TTranslate): string {
  const date = invoice.issuedAt ?? invoice.dueAt ?? invoice.paidAt;
  return date ? dateTodMMMyyyy(date) : t('billing.invoices.fallbackDate');
}

export function getBillingInvoiceProductLabel(invoice: Components.Schemas.BillingInvoiceDto, t: TTranslate): string {
  return invoice.product ? t(productMetaByProduct[invoice.product].titleKey) : t('billing.invoices.fallbackProduct');
}

export function getBillingInvoicePdfFileName(invoice: Components.Schemas.BillingInvoiceDto): string {
  return buildPdfFileName('invoice', invoice.invoiceNumber ?? invoice.billingInvoiceId);
}

export function formatBillingInvoiceAmount(invoice: Components.Schemas.BillingInvoiceDto): string {
  return formatBillingAmount(invoice.amountDue, invoice.currency);
}

type BillingAddressDraftSource = Pick<
  Components.Schemas.BillingInvoiceAddressDto,
  'name' | 'line1' | 'city' | 'postalCode' | 'vatId' | 'notes'
>;

export function getBillingInvoiceAddressDraft(
  address: BillingAddressDraftSource | null | undefined,
): Components.Schemas.UpsertTenantInvoiceAddressDto | null {
  if (!address) return null;
  return {
    name: address.name,
    line1: address.line1,
    city: address.city,
    postalCode: address.postalCode,
    vatId: address.vatId ?? null,
    notes: address.notes ?? null,
  };
}
